import { useCallback, useRef, useState } from 'react'
import { useReactToPrint } from 'react-to-print'

// Utils
import { toast } from '../utils/helpers'

/**
 * usePrintableReport
 *
 * Drives one printable report: the ref to hang on its container, a flag the report
 * reads to switch to print sizing, and a trigger to hand to the button.
 *
 * Replaces the pattern this repo had at five call sites — flip a `printing` flag,
 * `setTimeout(handlePrint, 200)` to let the re-render land, hope for the best — which
 * had three problems (EXEC-1592):
 *
 * - The timeout put `window.print()` outside the user-gesture window. Safari is the
 *   strict one about that, and Safari is where the failure was reported. Flipping the
 *   flag inside `onBeforeGetContent` keeps the whole thing in react-to-print's own
 *   promise chain, which is what that callback exists for, and drops the guesswork
 *   about whether 200ms was enough.
 * - Nothing handled failure. `onPrintError` was never passed and react-to-print
 *   swallows its errors to the console, so a failed print looked like a dead button.
 * - `printing` was only cleared in `onAfterPrint`, so any failure left the report stuck
 *   in print sizing until a reload.
 */
const usePrintableReport = ({ label = 'report' } = {}) => {
  const contentRef = useRef()
  const [printing, setPrinting] = useState(false)

  const failed = useCallback(() => {
    setPrinting(false)
    toast(`We couldn't open the print dialog for this ${label}. Please try again.`, 'error')
  }, [label])

  const handlePrint = useReactToPrint({
    content: () => contentRef.current,
    // Resolves after React has committed the print-sized render and the browser has
    // painted it, so react-to-print clones the report at its print size
    onBeforeGetContent: () =>
      new Promise((resolve) => {
        setPrinting(true)
        requestAnimationFrame(() => requestAnimationFrame(resolve))
      }),
    onAfterPrint: () => setPrinting(false),
    onPrintError: failed,
  })

  const print = useCallback(() => {
    // The button is disabled until the report has data, so this is a guard against a
    // race rather than an expected state — but silently doing nothing is the exact
    // symptom being fixed, so it gets the same message.
    if (!contentRef.current) {
      failed()
      return
    }

    handlePrint()
  }, [failed, handlePrint])

  return { contentRef, printing, print, setPrinting }
}

export default usePrintableReport
